import axios from "axios";
import React, { useEffect, useState, useRef, useMemo } from "react";
import {
    MDBTabsPane,
    MDBInput,
    MDBDropdown,
    MDBDropdownItem,
    MDBDropdownMenu,
    MDBDropdownToggle,
    MDBSpinner,
    MDBIcon,
} from "mdb-react-ui-kit";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { exportToPDF, exportToCSV } from "../../../helper/exportDatatable";
import { format, parseISO } from "date-fns";
import "jspdf-autotable";
import VoteTabulationModal from "../Modals/VoteTabulationModal";
import CommitteeName, { committeeCache } from "../Info/Committee/CommitteeName";
import CommitteeHearingDate from "../Info/Committee/CommitteeHearingDate";

let globalMeetingIndex = 0;
const usedMeetingIndices = new Set();

const convertISOToReadableTimeFormat = (date) => {
    return format(parseISO(date), "MM/dd/yy");
};

const exportFormatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
    const month = months[date.getMonth()];
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';

    hours = hours % 12;
    hours = hours ? hours : 12;
    hours = hours.toString().padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
};

const isHearingStatus = (billStatusCode) => {
    if (!billStatusCode || !billStatusCode.name) {
        return false;
    }

    const statusName = billStatusCode.name.trim().toLowerCase();
    return statusName === "(h) hearing" || statusName === "(s) hearing";
}

const isHb2 = (billType, billId) => {
    if (billType.toLowerCase() === "hb" && billId === 2) {
        return true;
    }
    return false
}

const formatRowStructureForStatusDatatable = async ({
    billExecutiveActionVoteData = [],
    billFloorVoteData = [],
    activeSessionInfo,
    billActions = [],
    billId,
    showVoteTabulationUI,
    setShowVoteTabulationUI,
    meetingData = [],
}) => {
    // Reset used indices when formatting new data
    usedMeetingIndices.clear();
    globalMeetingIndex = 0;

    const hearingMeetingCache = {};

    // Fetch and cache committee names first
    const committeeIds = billActions
        .filter(action => action.standingCommitteeId)
        .map(action => action.standingCommitteeId);

    if (committeeIds.length > 0) {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommittees/search?limit=100&offset=0`,
                {
                    standingCommitteeIds: committeeIds,
                    legislatureIds: [2]
                },
                {
                    headers: {
                        'accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Populate the cache
            response.data?.content?.forEach(committee => {
                const cacheKey = `${committee.id}-2`;
                committeeCache[cacheKey] = committee.committeeDetails?.committeeCode?.name || 'Committee name unavailable';
            });
        } catch (error) {
            console.error("Error fetching committee data: ", error);
        }
    }

    return billActions.map((item, index) => {
        const actionDescription = item?.billStatusCode?.name || "";

        const committeeId = item?.standingCommitteeId;
        let committeeName = "";
        if (committeeId) {
            const cacheKey = `${committeeId}-2`;
            committeeName = committeeCache[cacheKey] || "Unknown Committee";
        }

        // Get meeting data for this status
        let meetingInfo = null;
        let hearingDateText = '';
        if (isHearingStatus(item.billStatusCode)) {
            let meetingIndex;
            // Find the next available meeting index, starting from the beginning
            for (let i = 0; i < meetingData.length; i++) {
                if (!usedMeetingIndices.has(i)) {
                    meetingIndex = i;
                    usedMeetingIndices.add(i);
                    break;
                }
            }
            
            if (meetingIndex !== undefined) {
                const meeting = meetingData[meetingIndex];
                meetingInfo = {
                    meetingIndex: meetingIndex,
                    meetingTime: meeting?.committeeMeeting?.meetingTime
                };
                if (meeting?.committeeMeeting) {
                    const formattedTime = exportFormatDate(meeting.committeeMeeting.meetingTime);
                    const location = meeting.committeeMeeting.location || '';
                    hearingDateText = `${formattedTime} ${location}`;
                }
            }
        }

        // Determine vote counts
        const matchingRecord = billExecutiveActionVoteData.find(
            (record) => record?.billStatusId === item?.id
        );
        const matchingFloorVoteRecord = billFloorVoteData.find(
            (record) => record?.billStatus?.id === item?.id
        );

        let yesVotes = 0;
        let noVotes = 0;
        let hasVotes = false;

        if (matchingRecord && Array.isArray(matchingRecord.legislatorVotes)) {
            const voteCount = matchingRecord.legislatorVotes.reduce(
                (count, vote) => {
                    if ((vote.committeeVote === "YES") ||
                        (vote.committeeVote === "YES_BY_PROXY") ||
                        (vote.committeeVote === "YES_EXCUSED"))
                        count.yes++;
                    else if ((vote.committeeVote === "NO") ||
                        (vote.committeeVote === "NO_BY_PROXY") ||
                        (vote.committeeVote === "NO_VOTE") ||
                        (vote.committeeVote === "NO_EXCUSED"))
                        count.no++;
                    return count;
                },
                { yes: 0, no: 0 }
            );
            yesVotes = voteCount.yes;
            noVotes = voteCount.no;
            hasVotes = yesVotes > 0 || noVotes > 0;
        } else if (
            matchingFloorVoteRecord &&
            Array.isArray(matchingFloorVoteRecord.legislatorVotes)
        ) {
            const floorVoteCount = matchingFloorVoteRecord.legislatorVotes.reduce(
                (count, vote) => {
                    if ((vote.voteType === "YES") ||
                        (vote.voteType === "YES_BY_PROXY") ||
                        (vote.voteType === "YES_EXCUSED"))
                        count.yes++;
                    else if ((vote.voteType === "NO") ||
                        (vote.voteType === "NO_BY_PROXY") ||
                        (vote.voteType === "NO_EXCUSED"))
                        count.no++;
                    return count;
                },
                { yes: 0, no: 0 }
            );
            yesVotes = floorVoteCount.yes;
            noVotes = floorVoteCount.no;
            hasVotes = yesVotes > 0 || noVotes > 0;
        }

        const voteProps = {
            showVoteTabulationUI,
            setShowVoteTabulationUI,
            matchingRecord,
            matchingFloorVoteRecord,
            activeSessionInfo,
        };

        return {
            ...item,
            statusDateRaw: new Date(item?.timeStamp),
            statusDateFormatted: convertISOToReadableTimeFormat(item?.timeStamp),
            Action: actionDescription,
            Committee: committeeId && !actionDescription.includes("Committee Report") ? <CommitteeName standingCommitteeId={committeeId} /> : null,
            CommitteeText: committeeName,
            HearingDateRaw: meetingInfo?.meetingTime || item.hearingDate,
            HearingDateFormatted: hearingDateText,
            meetingInfo: meetingInfo,
            VotesYesRaw: hasVotes ? yesVotes : 0,
            VotesNoRaw: hasVotes ? noVotes : 0,
            "Votes (Yes)": hasVotes ? (
                <VoteDataLink
                    voteCount={yesVotes}
                    voteType="yes"
                    iconType="check-circle"
                    {...voteProps}
                />
            ) : null,
            "Votes (No)": hasVotes ? (
                <VoteDataLink
                    voteCount={noVotes}
                    voteType="no"
                    iconType="times-circle"
                    {...voteProps}
                />
            ) : null,
            MediaRaw: typeof item?.media === "string" ? item.media : "",
            Media: item?.media || "",
        };
    });
};

const formatRowStructureForExport = async (billActions, billId, billExecutiveActionVoteData, billFloorVoteData) => {
    const committeeMeetingCache = {};
    const usedExportMeetingIndices = new Set();

    async function fetchStandingCommitteeMeetingDate(billId) {
        const cacheKey = `findByBillId-${billId}`;
        if (committeeMeetingCache[cacheKey]) {
            return committeeMeetingCache[cacheKey];
        }
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommitteeMeetingBillHearings/findByBillId?billId=${billId}`,
            );
            committeeMeetingCache[cacheKey] = response.data;
            return response.data;
        } catch (error) {
            console.error("Error fetching committee meeting data: ", error);
            return null;
        }
    }

    // Fetch all meeting data upfront
    let meetingData = [];
    if (billId) {
        try {
            const response = await fetchStandingCommitteeMeetingDate(billId);
            if (response && Array.isArray(response)) {
                // Sort meetings by meeting time in descending order
                meetingData = response.sort((a, b) => 
                    new Date(b.committeeMeeting?.meetingTime) - new Date(a.committeeMeeting?.meetingTime)
                );
            }
        } catch (error) {
            console.error("Error fetching meeting data:", error);
        }
    }

    const getVoteCounts = (item) => {
        const matchingRecord = billExecutiveActionVoteData.find(record => record?.billStatusId === item?.id);
        const matchingFloorVoteRecord = billFloorVoteData.find(record => record?.billStatus?.id === item?.id);

        let yesVotes = 0;
        let noVotes = 0;
        let hasVotes = false;
        if (matchingRecord && Array.isArray(matchingRecord.legislatorVotes)) {
            const voteCount = matchingRecord.legislatorVotes.reduce((count, vote) => {
                console.log(vote.committeeVote)
                if ((vote.committeeVote === "YES") ||
                    (vote.committeeVote === "YES_BY_PROXY") ||
                    (vote.committeeVote === "YES_EXCUSED"))
                    count.yes++;
                else if ((vote.committeeVote === "NO") ||
                    (vote.committeeVote === "NO_BY_PROXY") ||
                    (vote.committeeVote === "NO_VOTE") ||
                    (vote.committeeVote === "NO_EXCUSED"))
                    count.no++;
                return count;
            }, {yes: 0, no: 0});
            yesVotes = voteCount.yes;
            noVotes = voteCount.no;
            hasVotes = yesVotes > 0 || noVotes > 0;
        } else if (matchingFloorVoteRecord && Array.isArray(matchingFloorVoteRecord.legislatorVotes)) {
            const floorVoteCount = matchingFloorVoteRecord.legislatorVotes.reduce((count, vote) => {
                if ((vote.voteType === "YES") ||
                    (vote.voteType === "YES_BY_PROXY") ||
                    (vote.voteType === "YES_EXCUSED"))
                    count.yes++;
                else if ((vote.voteType === "NO") ||
                    (vote.voteType === "NO_BY_PROXY") ||
                    (vote.voteType === "NO_EXCUSED"))
                    count.no++;
                return count;
            }, {yes: 0, no: 0});
            yesVotes = floorVoteCount.yes;
            noVotes = floorVoteCount.no;
            hasVotes = yesVotes > 0 || noVotes > 0;
        }

        return { yesVotes, noVotes, hasVotes };
    };

    billActions.sort((a, b) => new Date(b.timeStamp) - new Date(a.timeStamp));
    const committeePromises = billActions.map(async (item) => {
        let committeeName = '';
        let hearingDate = '';

        if (item.standingCommitteeId) {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommittees/search?limit=1&offset=0`,
                    {
                        standingCommitteeIds: [item.standingCommitteeId],
                        legislatureIds: [2]
                    },
                    {
                        headers: {
                            'accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    }
                );
                committeeName = response.data?.content?.[0]?.committeeDetails?.committeeCode?.name || 'Committee name unavailable';
            } catch (error) {
                console.error("Error fetching committee data: ", error);
                committeeName = 'Committee name unavailable';
            }
        }

        if (isHearingStatus(item.billStatusCode)) {
            let meetingIndex;
            // Find the next available meeting index, starting from the beginning
            for (let i = 0; i < meetingData.length; i++) {
                if (!usedExportMeetingIndices.has(i)) {
                    meetingIndex = i;
                    usedExportMeetingIndices.add(i);
                    break;
                }
            }

            if (meetingIndex !== undefined) {
                const meeting = meetingData[meetingIndex];
                if (meeting && meeting.committeeMeeting) {
                    let showCancelled = "";
                    if (meeting.status === "CANCELED") {
                        showCancelled = " - CANCELED";
                    }
                    const formattedTime = exportFormatDate(meeting.committeeMeeting.meetingTime);
                    const location = meeting.committeeMeeting.location || 'Location not available';
                    hearingDate = `${formattedTime}${showCancelled} | ${location}`;
                }
            }
        }

        const { yesVotes, noVotes, hasVotes } = getVoteCounts(item);

        return {
            "Status Date": exportFormatDate(item?.timeStamp) || '',
            "Action": item?.billStatusCode?.name || '',
            "Committee": item?.billStatusCode?.name?.includes("Committee Report") ? '' : committeeName,
            "Hearing Date": hearingDate,
            "Votes (Yes)": hasVotes ? yesVotes : '',
            "Votes (No)": hasVotes ? noVotes : '',
            "Media": item?.media ? 'Media' : ''
        };
    });

    return Promise.all(committeePromises);
};

const VoteDataLink = ({
                          voteCount,
                          showVoteTabulationUI,
                          setShowVoteTabulationUI,
                          matchingRecord,
                          matchingFloorVoteRecord,
                          iconType,
                      }) => {
    const createLink = (id) => {
        const displayCount = voteCount;
        const billStatusId =
            matchingRecord?.billStatusId ?? matchingFloorVoteRecord?.billStatus?.id;
        const voteTime =
            matchingRecord?.voteTime ?? matchingFloorVoteRecord?.voteTime;

        return (
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    setShowVoteTabulationUI({
                        show: true,
                        billStatusId: billStatusId,
                        voteTime: voteTime,
                    });
                }}
            >
                <MDBIcon
                    far
                    icon={iconType}
                    className="me-2"
                    style={{ color: iconType === "check-circle" ? "#67897f" : "#8a5f68" }}
                />
                {displayCount}
            </a>
        );
    };

    const link = createLink();
    if (link === null) {
        return voteCount;
    }

    return <>{link}</>;
};

const Status = ({ active, activeSessionInfo, bill }) => {
    const [showVotesOnly, setShowVotesOnly] = useState(false);
    const [formattedData, setFormattedData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [billExecutiveActionVoteData, setBillExecutiveActionVoteData] =
        useState([]);
    const [billFloorVoteData, setBillFloorVoteData] = useState([]);
    const [meetingData, setMeetingData] = useState([]);
    const [showVoteTabulationUI, setShowVoteTabulationUI] = useState({
        show: false,
        billStatusId: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const meetingIndexRef = useRef(0);

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        statusDateFormatted: { value: null, matchMode: FilterMatchMode.CONTAINS },
        Action: { value: null, matchMode: FilterMatchMode.CONTAINS },
        CommitteeText: { value: null, matchMode: FilterMatchMode.CONTAINS },
        HearingDateFormatted: { value: null, matchMode: FilterMatchMode.CONTAINS },
        VotesYesRaw: { value: null, matchMode: FilterMatchMode.EQUALS },
        VotesNoRaw: { value: null, matchMode: FilterMatchMode.EQUALS },
        MediaRaw: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    // Reset globalMeetingIndex when bill changes
    useEffect(() => {
        globalMeetingIndex = 0;
    }, [bill.id]);

    useEffect(() => {
        async function fetchAllData() {
            setLoading(true);
            try {
                const [executiveActionData, floorVoteData] = await Promise.all([
                    axios.get(
                        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/executiveActions/findByBillId?billId=${bill.id}`
                    ),
                    axios.get(
                        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/bills/v1/votes/findByBillId?billId=${bill.id}`
                    ),
                ]);
                setBillExecutiveActionVoteData(executiveActionData.data);
                setBillFloorVoteData(floorVoteData.data);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchAllData();
    }, [bill.id]);

    useEffect(() => {
        async function fetchMeetingData() {
            if (bill.id) {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommitteeMeetingBillHearings/findByBillId?billId=${bill.id}`,
                    );
                    if (response.data && Array.isArray(response.data)) {
                        // Sort meetings by meeting time in descending order
                        const sortedMeetings = response.data.sort((a, b) => 
                            new Date(b.committeeMeeting?.meetingTime) - new Date(a.committeeMeeting?.meetingTime)
                        );
                        setMeetingData(sortedMeetings);
                    }
                } catch (error) {
                    console.error("Error fetching meeting data:", error);
                    setMeetingData([]);
                }
            }
        }
        fetchMeetingData();
    }, [bill.id]);

    useEffect(() => {
        async function processBillActions() {
            try {
                const excludedFiscalCodes = process.env.REACT_APP_FISCAL_NOTE_CODES.split(',').map(Number);
                const processedActions = bill.draft.billStatuses
                    .filter((billAction) => {
                        const code = parseInt(billAction?.billStatusCode?.code, 10);
                        return !excludedFiscalCodes.includes(code);
                    })
                    .sort((a, b) => new Date(b.timeStamp) - new Date(a.timeStamp))
                    .map((billAction) => ({
                        ...billAction,
                        Date: convertISOToReadableTimeFormat(billAction?.timeStamp),
                        Action: billAction?.billStatusCode?.name || "",
                        timestamp: billAction?.timeStamp,
                    }));

                const formattedRow = await formatRowStructureForStatusDatatable({
                    billExecutiveActionVoteData,
                    billFloorVoteData,
                    activeSessionInfo,
                    billActions: processedActions,
                    billId: bill.id,
                    showVoteTabulationUI,
                    setShowVoteTabulationUI,
                    meetingData,
                });

                // Ensure formattedData is an array (PrimeReact requirement)
                setFormattedData(formattedRow);
                setOriginalData(formattedRow);
            } catch (error) {
                console.error("Error processing bill actions: ", error);
                setError("Failed to process bill actions");
            }
        }

        if (!loading && !error) {
            processBillActions();
        }
    }, [billExecutiveActionVoteData, billFloorVoteData, loading, error, meetingData]);

    useEffect(() => {
        if (showVotesOnly) {
            setFormattedData(
                originalData.filter(
                    (row) => row["Votes (Yes)"] > 0 || row["Votes (No)"] > 0
                )
            );
        } else {
            setFormattedData(originalData);
        }
    }, [showVotesOnly, originalData]);

    useEffect(() => {
        const getVoteCount = (vote) => {
            if (typeof vote === "string") {
                const trimmedVote = vote.trim();
                return trimmedVote === "" ? 0 : parseInt(trimmedVote, 10) || 0;
            }
            if (React.isValidElement(vote)) {
                return vote.props.voteCount || 0;
            }
            return 0;
        };

        let newRows;
        if (showVotesOnly) {
            newRows = originalData.filter((row) => {
                const yesVotes = getVoteCount(row["Votes (Yes)"]);
                const noVotes = getVoteCount(row["Votes (No)"]);
                return yesVotes > 0 || noVotes > 0;
            });
        } else {
            newRows = originalData;
        }

        setFormattedData(newRows);
    }, [showVotesOnly, originalData]);

    // Apply row class based on vote outcome
    const getRowClass = (rowData) => {
        const extractVoteCount = (vote) => {
            if (typeof vote === "number") return vote; // Already a number
            if (typeof vote === "string") return parseInt(vote, 10) || 0; // If it's a string, convert to number
            if (React.isValidElement(vote) && vote.props?.voteCount) {
                return parseInt(vote.props.voteCount, 10) || 0; // Extract from VoteDataLink
            }
            return 0; // Default fallback
        };

        const yesVotes = extractVoteCount(rowData["Votes (Yes)"]);
        const noVotes = extractVoteCount(rowData["Votes (No)"]);

        // If bill status is "Taken from Committee; Placed on 2nd Reading"
        // special rule
        if (
            (rowData.billStatusCode.code == 550 || rowData.billStatusCode.code == 549) &&
            yesVotes < 55
        ) {
            return "no-leading";
        }
        // Constitutional Amendments Vote Majority is 100 votes for both chambers is passing anything under fails.
        // special rule
        if (
            (rowData.billStatusCode.code == 296 || rowData.billStatusCode.code == 696) &&
            yesVotes < 100
        ) {
            return "no-leading";
        }


        if (yesVotes > noVotes) return "yes-leading";
        if (noVotes > yesVotes) return "no-leading";
        if (yesVotes === noVotes && yesVotes !== 0) return "tie-leading";

        return "";
    };

    //Spinner
    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "100vh" }}
            >
                <MDBSpinner role="status" size="lg">
                    <span className="visually-hidden">Loading...</span>
                </MDBSpinner>
            </div>
        );
    }

    // Update filters when searching
    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilter(value);
    };

    //Exports
    const exportColumns = [
        { field: "Status Date", header: "Status Date" },
        { field: "Action", header: "Action" },
        { field: "Committee", header: "Committee" },
        { field: "Hearing Date", header: "Hearing Date" },
        { field: "Votes (Yes)", header: "Yes Votes" },
        { field: "Votes (No)", header: "No Votes" },
    ];

    const handlePDFExport = async () => {
        const exportData = await formatRowStructureForExport(
            bill.draft.billStatuses,
            bill.id,
            billExecutiveActionVoteData,
            billFloorVoteData
        );
        exportToPDF(
            "Status",
            `${bill?.billType?.code}${bill?.billNumber}`,
            exportColumns,
            exportData,
            {
                columnStyles: { 4: { halign: "center" }, 5: { halign: "center" } },
                centerHeaders: ["Yes Votes", "No Votes"],
            }
        );
    };

    const handleCSVExport = async () => {
        const exportData = await formatRowStructureForExport(
            bill.draft.billStatuses,
            bill.id,
            billExecutiveActionVoteData,
            billFloorVoteData
        );
        exportToCSV(
            "Status",
            `${bill?.billType?.code}${bill?.billNumber}`,
            exportColumns,
            exportData
        );
    };

    return (
        <MDBTabsPane show={active}>
            <VoteTabulationModal
                floorVoteData={billFloorVoteData}
                voteData={billExecutiveActionVoteData}
                activeSessionInfo={activeSessionInfo}
                bill={bill}
                showVoteTabulationUI={showVoteTabulationUI}
                setShowVoteTabulationUI={setShowVoteTabulationUI}
            />
            <div className="row g-1 align-items-center mb-3">
                <div className="col-sm">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        checked={showVotesOnly}
                        onChange={() => setShowVotesOnly(!showVotesOnly)}
                    />
                    <label className="form-check-label"> Show Only Votes</label>
                </div>
                <div className="col-sm">
                    <MDBInput
                        label="Search Status..."
                        type="text"
                        value={globalFilter || ""}
                        onChange={onGlobalFilterChange}
                    />
                </div>
                <div className="col-auto">
                    <MDBDropdown>
                        <MDBDropdownToggle>
                            <i
                                className="fa-light fa-cloud-arrow-down"
                                aria-hidden="true"
                            ></i>{" "}
                            Export
                        </MDBDropdownToggle>
                        <MDBDropdownMenu>
                            <MDBDropdownItem
                                link
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleCSVExport();
                                }}
                            >
                                Excel/CSV
                            </MDBDropdownItem>
                            <MDBDropdownItem
                                link
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePDFExport();
                                }}
                            >
                                PDF/Print
                            </MDBDropdownItem>
                        </MDBDropdownMenu>
                    </MDBDropdown>
                </div>
            </div>
            <DataTable
                value={formattedData}
                rowClassName={getRowClass}
                paginator
                rows={100}
                rowsPerPageOptions={[25, 50, 100]}
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                stripedRows
                columnResizeMode="expand"
                sortField="statusDateRaw"
                sortOrder={-1}
                removableSort
                loading={loading}
                filters={filters}
                globalFilterFields={['statusDateFormatted', 'Action', 'CommitteeText', 'HearingDateFormatted', 'VotesYesRaw', 'VotesNoRaw', 'MediaRaw']}
            >
                <Column
                    field="statusDateRaw" // Use raw date for sorting
                    header="Status Date"
                    sortable
                    filter
                    filterField="statusDateFormatted" // Use formatted text for filtering
                    body={(rowData) => rowData.statusDateFormatted} // Show formatted date
                />
                <Column field="Action" header="Action" sortable filter />
                <Column
                    field="CommitteeText"
                    header="Committee"
                    sortable
                    filter
                    body={(rowData) => (
                        rowData.standingCommitteeId && !rowData.Action.includes("Committee Report") ? (
                            <CommitteeName 
                                committeeId={rowData.standingCommitteeId}
                                legislatureId={2}
                            />
                        ) : null
                    )}
                />
                <Column
                    field="HearingDateRaw"
                    header="Hearing Date"
                    sortable
                    filter
                    filterField="HearingDateFormatted"
                    body={(rowData) => {
                        if (isHb2(bill.billType.code, bill.billNumber)) {
                            // Logic for HB2 hearing data
                            if (isHearingStatus(rowData.billStatusCode) && rowData.meetingInfo) {
                                return (
                                    <a
                                        href={`${process.env.REACT_APP_COMMITTEE_EXPLORER_URI}/#/standingCommittees/${rowData?.standingCommitteeId}?tab=Meetings`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        See Hearing Times
                                    </a>
                                );
                            }
                            return null;
                        } else {
                            // Logic for non-HB2 hearing data
                            if (isHearingStatus(rowData.billStatusCode) && rowData.meetingInfo) {
                                return (
                                    <CommitteeHearingDate
                                        key={`hearing-${rowData.id}-${rowData.meetingInfo.meetingIndex}`}
                                        billId={bill.id}
                                        billStatusCode={rowData.billStatusCode}
                                        statusDate={rowData.timeStamp}
                                        meetingIndex={rowData.meetingInfo.meetingIndex}
                                    />
                                );
                            }
                            return null;
                        }
                    }}
                />
                <Column
                    field="VotesYesRaw"
                    header="Yes Votes"
                    dataType="numeric"
                    sortable
                    filter
                    body={(rowData) => rowData["Votes (Yes)"]}
                    bodyStyle={{ textAlign: "center" }}
                />
                <Column
                    field="VotesNoRaw"
                    header="No Votes"
                    dataType="numeric"
                    sortable
                    filter
                    body={(rowData) => rowData["Votes (No)"]}
                    bodyStyle={{ textAlign: "center" }}
                />
                <Column
                    field="MediaRaw"
                    header="Media"
                    sortable
                    filter
                    body={(rowData) => rowData.Media}
                    bodyStyle={{ textAlign: "center" }}
                />
            </DataTable>
        </MDBTabsPane>
    );
};

export default Status;
